import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormField,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@shared/ui';
import { CountrySelect } from '../country-select';
import type { ProfileFormValues } from '../../model/schema';

const EMPLOYEES: ('XS' | 'S' | 'M' | 'L' | 'XL')[] = ['XS', 'S', 'M', 'L', 'XL'];

export function BasicTab() {
  const { t } = useTranslation();
  const form = useFormContext<ProfileFormValues>();
  const errors = form.formState.errors;

  return (
    <div className="grid gap-5 md:grid-cols-2">
      <FormField label={t('profile.fields.companyName')} required error={errors.companyName?.message} className="md:col-span-2">
        {(p) => <Input {...p} {...form.register('companyName')} />}
      </FormField>

      <FormField label={t('profile.fields.description')} error={errors.description?.message} className="md:col-span-2">
        {(p) => <Textarea {...p} rows={4} {...form.register('description')} />}
      </FormField>

      <FormField label={t('profile.fields.country')} error={errors.country?.message}>
        {(p) => (
          <Controller
            control={form.control}
            name="country"
            render={({ field }) => (
              <CountrySelect
                id={p.id}
                invalid={p.invalid}
                aria-describedby={p['aria-describedby']}
                value={field.value ?? null}
                onChange={field.onChange}
              />
            )}
          />
        )}
      </FormField>

      <FormField label={t('profile.fields.website')} error={errors.website?.message}>
        {(p) => (
          <Input {...p} type="url" placeholder="https://example.com" {...form.register('website')} />
        )}
      </FormField>

      <FormField label={t('profile.fields.foundedYear')} error={errors.foundedYear?.message}>
        {(p) => (
          <Input
            {...p}
            type="number"
            inputMode="numeric"
            {...form.register('foundedYear', {
              setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
            })}
          />
        )}
      </FormField>

      <FormField label={t('profile.fields.employees')} error={errors.employeesRange?.message}>
        {(p) => (
          <Controller
            control={form.control}
            name="employeesRange"
            render={({ field }) => (
              <Select
                value={field.value ?? undefined}
                onValueChange={(v) => field.onChange(v || null)}
              >
                <SelectTrigger id={p.id} invalid={p.invalid}>
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYEES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {t(`profile.employees.${v}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}
      </FormField>
    </div>
  );
}
